import React, { useEffect, useState, useRef } from "react";
import {
    Stage,
    Layer,
    Rect,
    Line,
    Circle,
    Text,
    Arrow,
    Transformer,
    Image as KonvaImage,
    Group,
} from "react-konva";
import {
    ToggleButton,
    ToggleButtonGroup,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Tooltip,
    Button,
    Dialog,
    DialogContent,
    CircularProgress,
} from "@mui/material";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import DrawIcon from "@mui/icons-material/Gesture";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import RemoveIcon from "@mui/icons-material/Remove";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FlipToFrontIcon from "@mui/icons-material/FlipToFront";
import FlipToBackIcon from "@mui/icons-material/FlipToBack";
import ImageIcon from "@mui/icons-material/Image";
import firebase from "../../firebase";

const TOOL_OPTIONS = [
    { value: "select", icon: <HighlightAltIcon />, label: "Select" },
    { value: "pen", icon: <DrawIcon />, label: "Pen" },
    { value: "text", icon: <TextFieldsIcon />, label: "Text" },
    { value: "circle", icon: <RadioButtonUncheckedIcon />, label: "Circle" },
    { value: "line", icon: <RemoveIcon />, label: "Line" },
    { value: "arrow", icon: <ArrowForwardIcon />, label: "Arrow" },
    { value: "rectangle", icon: <CropSquareIcon />, label: "Rectangle" },
];

const COLORS = [
    { value: "#000000", label: "Black" },
    { value: "#ff0000", label: "Red" },
    { value: "#00ff00", label: "Green" },
    { value: "#0000ff", label: "Blue" },
    { value: "#ffa500", label: "Orange" },
    { value: "#ffff00", label: "Yellow" },
    { value: "#800080", label: "Purple" },
    { value: "#ffffff", label: "White" },
    { value: "#00000000", label: "clear" },
];

const MAX_HISTORY = 50;
// How far a capture may be enlarged past its native size to fill the drawing
// area. Video frames are usually small; past roughly double they get too soft
// to annotate accurately.
const MAX_CANVAS_UPSCALE = 2;
// Breathing room kept below the canvas so the surround never runs off-screen.
const CANVAS_BOTTOM_GUTTER = 24;
const MIN_CANVAS_HEIGHT = 320;
const TEXT_PADDING = 10;
const TEXT_CORNER_RADIUS = 8;

export default function AnnotationCanvas({
    capturedImage,
    onSave,
    initialAnnotationData,
    playlistKey,
}) {
    const [selectedId, setSelectedId] = useState(null);
    const transformerRef = useRef();
    const shapeRef = useRef();
    const stageRef = useRef();
    const fileInputRef = useRef();

    const isEditingText = useRef(false);
    const copiedShape = useRef(null);
    const originalImageDimensions = useRef({ width: 0, height: 0 });
    const nextZIndex = useRef(0);

    const [bgImage, setBgImage] = useState(null);
    const [stageDimensions, setStageDimensions] = useState({
        width: 800,
        height: 600,
    });
    // The stage is sized from the space the surround actually has, so the
    // drawing area grows with the window instead of sitting at a fixed
    // fraction of it. Kept in a ref as well as state so the sizing maths can
    // read fresh numbers from inside callbacks.
    const canvasAreaRef = useRef(null);
    const canvasAreaMetrics = useRef(null);
    const [canvasAreaVersion, setCanvasAreaVersion] = useState(0);
    const [exportScale, setExportScale] = useState(1);

    const [tool, setTool] = useState("select");

    const [rectangles, setRectangles] = useState([]);
    const [newRect, setNewRect] = useState(null);

    const [circles, setCircles] = useState([]);
    const [newCircle, setNewCircle] = useState(null);

    const [freeLines, setFreeLines] = useState([]);
    const [newFreeLine, setNewFreeLine] = useState(null);

    const [texts, setTexts] = useState([]);

    const [lines, setLines] = useState([]);
    const [newLine, setNewLine] = useState(null);

    const [arrows, setArrows] = useState([]);
    const [newArrow, setNewArrow] = useState(null);

    const [images, setImages] = useState([]);

    const [strokeColor, setStrokeColor] = useState("#000000");
    const [fillColor, setFillColor] = useState("#ff0000");
    const [fillOpacity, setFillOpacity] = useState(1);

    const [lineWidth, setLineWidth] = useState(2);
    const [lineIsDashed, setLineIsDashed] = useState(false);

    const [fontFamily, setFontFamily] = useState("Arial");
    const [fontSize, setFontSize] = useState(18);

    const [history, setHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const isUndoRedoAction = useRef(false);
    const isInitialLoad = useRef(true);

    // Loading states
    const [isImportingImage, setIsImportingImage] = useState(false);
    const [isLoadingAnnotation, setIsLoadingAnnotation] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const calculateDimensions = (imgWidth, imgHeight) => {
        const area = canvasAreaMetrics.current;

        // Before the surround has been measured, fall back to the viewport so
        // the first paint still has sensible dimensions.
        const maxWidth = area ? area.width : window.innerWidth * 0.7;
        const maxHeight = area ? area.height : window.innerHeight * 0.6;

        let width = imgWidth;
        let height = imgHeight;

        const widthRatio = maxWidth / width;
        const heightRatio = maxHeight / height;

        // Captured video frames are often far smaller than the space available,
        // so allow some enlargement to make them workable to draw on, capped
        // before the frame turns to mush.
        const scale = Math.min(widthRatio, heightRatio, MAX_CANVAS_UPSCALE);

        width = width * scale;
        height = height * scale;

        return { width, height, scale };
    };

    const getAllShapesSorted = () => {
        const allShapes = [
            ...freeLines.map((s) => ({ ...s, type: "freeLine" })),
            ...rectangles.map((s) => ({ ...s, type: "rectangle" })),
            ...circles.map((s) => ({ ...s, type: "circle" })),
            ...lines.map((s) => ({ ...s, type: "line" })),
            ...arrows.map((s) => ({ ...s, type: "arrow" })),
            ...texts.map((s) => ({ ...s, type: "text" })),
            ...images.map((s) => ({ ...s, type: "image" })),
        ];
        return allShapes.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    };

    const updateSelectedShapeProperty = (property, value) => {
        if (!selectedId) return;

        setRectangles((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, [property]: value } : s
            )
        );
        setCircles((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, [property]: value } : s
            )
        );
        setFreeLines((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, [property]: value } : s
            )
        );
        setTexts((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, [property]: value } : s
            )
        );
        setLines((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, [property]: value } : s
            )
        );
        setArrows((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, [property]: value } : s
            )
        );
        setImages((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, [property]: value } : s
            )
        );
    };

    const handleStrokeColorChange = (value) => {
        setStrokeColor(value);
        updateSelectedShapeProperty("stroke", value);
        // For text, update the text fill color
        setTexts((prev) =>
            prev.map((s) => (s.id === selectedId ? { ...s, fill: value } : s))
        );
    };

    const handleFillColorChange = (value) => {
        setFillColor(value);

        // Update fill for rectangles and circles, but NOT for text
        setRectangles((prev) =>
            prev.map((s) => (s.id === selectedId ? { ...s, fill: value } : s))
        );
        setCircles((prev) =>
            prev.map((s) => (s.id === selectedId ? { ...s, fill: value } : s))
        );

        // For text, update the background fill color only
        setTexts((prev) =>
            prev.map((s) => (s.id === selectedId ? { ...s, bgFill: value } : s))
        );
    };

    const handleFillOpacityChange = (value) => {
        setFillOpacity(value);

        // Update opacity for rectangles and circles
        setRectangles((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, opacity: value } : s
            )
        );
        setCircles((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, opacity: value } : s
            )
        );

        // For text, update the background opacity
        setTexts((prev) =>
            prev.map((s) =>
                s.id === selectedId ? { ...s, bgOpacity: value } : s
            )
        );
    };

    const handleLineWidthChange = (value) => {
        setLineWidth(value);
        updateSelectedShapeProperty("strokeWidth", value);
    };

    const handleLineIsDashedChange = (value) => {
        setLineIsDashed(value);
        updateSelectedShapeProperty("dash", value ? [10, 5] : []);
    };

    const handleFontFamilyChange = (value) => {
        setFontFamily(value);
        updateSelectedShapeProperty("fontFamily", value);
    };

    const handleFontSizeChange = (value) => {
        setFontSize(value);
        updateSelectedShapeProperty("fontSize", value);
    };

    useEffect(() => {
        if (!selectedId) return;

        const selectedRect = rectangles.find((r) => r.id === selectedId);
        const selectedCircle = circles.find((c) => c.id === selectedId);
        const selectedFreeLine = freeLines.find((l) => l.id === selectedId);
        const selectedText = texts.find((t) => t.id === selectedId);
        const selectedLine = lines.find((l) => l.id === selectedId);
        const selectedArrow = arrows.find((a) => a.id === selectedId);

        if (
            selectedRect?.stroke ||
            selectedCircle?.stroke ||
            selectedFreeLine?.stroke ||
            selectedLine?.stroke ||
            selectedArrow?.stroke
        ) {
            const shape =
                selectedRect ||
                selectedCircle ||
                selectedFreeLine ||
                selectedLine ||
                selectedArrow;
            setStrokeColor(shape.stroke || "#000000");
        }

        if (selectedRect?.fill || selectedCircle?.fill) {
            const shape = selectedRect || selectedCircle;
            setFillColor(shape.fill || "#ff0000");
            setFillOpacity(shape.opacity !== undefined ? shape.opacity : 1);
        }

        if (
            selectedRect?.strokeWidth ||
            selectedCircle?.strokeWidth ||
            selectedFreeLine?.strokeWidth ||
            selectedLine?.strokeWidth ||
            selectedArrow?.strokeWidth
        ) {
            const shape =
                selectedRect ||
                selectedCircle ||
                selectedFreeLine ||
                selectedLine ||
                selectedArrow;
            setLineWidth(shape.strokeWidth || 2);
        }

        if (
            selectedRect?.dash ||
            selectedCircle?.dash ||
            selectedFreeLine?.dash ||
            selectedLine?.dash ||
            selectedArrow?.dash
        ) {
            const shape =
                selectedRect ||
                selectedCircle ||
                selectedFreeLine ||
                selectedLine ||
                selectedArrow;
            setLineIsDashed(shape.dash && shape.dash.length > 0);
        }

        if (selectedText) {
            setFontFamily(selectedText.fontFamily || "Arial");
            setFontSize(selectedText.fontSize || 18);
            setStrokeColor(selectedText.fill || "#000000");
            setFillColor(selectedText.bgFill || "#ff0000");
            setFillOpacity(
                selectedText.bgOpacity !== undefined
                    ? selectedText.bgOpacity
                    : 1
            );
        }
    }, [selectedId, rectangles, circles, freeLines, texts, lines, arrows]);

    const deleteImageFromStorage = async (storagePath) => {
        try {
            const storageRef = firebase.storage().ref();
            const imageRef = storageRef.child(storagePath);
            await imageRef.delete();
        } catch (error) {
            console.error("Error deleting image from storage:", error);
        }
    };

    const handleImageImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!playlistKey) {
            console.error("No playlist key provided");
            alert("Cannot upload image: No playlist key");
            return;
        }

        setIsImportingImage(true);

        try {
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substring(7);
            const filename = `annotations/${playlistKey}/imported_${timestamp}_${randomId}.jpg`;

            const storageRef = firebase.storage().ref();
            const imageRef = storageRef.child(filename);

            const uploadTask = await imageRef.put(file, {
                contentType: file.type || "image/jpeg",
            });

            const downloadURL = await uploadTask.ref.getDownloadURL();

            const img = new window.Image();
            img.crossOrigin = "anonymous";
            img.src = downloadURL;

            img.onload = () => {
                const maxSize = 300;
                let width = img.width;
                let height = img.height;

                if (width > maxSize || height > maxSize) {
                    const scale = Math.min(maxSize / width, maxSize / height);
                    width = width * scale;
                    height = height * scale;
                }

                const newImageId = `image-${Date.now()}`;
                const newImage = {
                    id: newImageId,
                    x: 100,
                    y: 100,
                    width,
                    height,
                    image: img,
                    firebaseUrl: downloadURL,
                    storagePath: filename,
                    zIndex: nextZIndex.current++,
                };

                setImages((prev) => [...prev, newImage]);
                setIsImportingImage(false);

                // Auto-select the imported image and switch to select tool
                setSelectedId(newImageId);
                setTool("select");
            };

            img.onerror = () => {
                console.error("Failed to load uploaded image");
                alert("Failed to load the uploaded image");
                setIsImportingImage(false);
            };
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Failed to upload image: " + error.message);
            setIsImportingImage(false);
        }

        e.target.value = "";
    };

    useEffect(() => {
        if (!capturedImage) return;

        // Only show loading dialog if we have annotation data to load (editing mode)
        if (initialAnnotationData) {
            setIsLoadingAnnotation(true);
        }

        const img = new window.Image();
        img.crossOrigin = "anonymous";
        img.src = capturedImage;

        img.onload = () => {
            setBgImage(img);

            originalImageDimensions.current = {
                width: img.width,
                height: img.height,
            };

            const { width, height, scale } = calculateDimensions(
                img.width,
                img.height
            );

            setStageDimensions({ width, height });
            setExportScale(1 / scale);

            // Only load annotation data if it exists (editing mode)
            if (initialAnnotationData) {
                loadAnnotationData(initialAnnotationData, scale);
            }
        };

        img.onerror = () => {
            console.error("Failed to load captured image");
            if (initialAnnotationData) {
                setIsLoadingAnnotation(false);
            }
        };
    }, [capturedImage, initialAnnotationData]);

    // Measure the surround rather than guessing at the viewport. Width comes
    // straight from the box; height is the room between the top of the box and
    // the bottom of the window, which does not depend on the canvas itself, so
    // growing the canvas cannot feed back into the measurement.
    useEffect(() => {
        const element = canvasAreaRef.current;

        if (!element) return undefined;

        const measure = () => {
            const rect = element.getBoundingClientRect();
            const styles = window.getComputedStyle(element);
            const horizontalPadding =
                parseFloat(styles.paddingLeft) +
                parseFloat(styles.paddingRight);
            const verticalPadding =
                parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);

            // Measured from the document rather than the viewport, so
            // scrolling the page does not resize the canvas underneath you.
            const documentTop = rect.top + window.scrollY;

            const width = Math.max(rect.width - horizontalPadding, 1);
            const height = Math.max(
                window.innerHeight -
                    documentTop -
                    verticalPadding -
                    CANVAS_BOTTOM_GUTTER,
                MIN_CANVAS_HEIGHT,
            );

            const previous = canvasAreaMetrics.current;

            if (
                previous &&
                Math.abs(previous.width - width) < 1 &&
                Math.abs(previous.height - height) < 1
            ) {
                return;
            }

            canvasAreaMetrics.current = { width, height };
            setCanvasAreaVersion((version) => version + 1);
        };

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        window.addEventListener("resize", measure);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, []);

    // Re-fit the stage (and everything drawn on it) whenever the measured
    // drawing area changes.
    useEffect(() => {
        if (!bgImage) return;

        const refit = () => {
            const { width, height, scale } = calculateDimensions(
                originalImageDimensions.current.width,
                originalImageDimensions.current.height
            );

            const oldWidth = stageDimensions.width;
            const scaleRatio = width / oldWidth;

            setStageDimensions({ width, height });
            setExportScale(1 / scale);

            setRectangles((prev) =>
                prev.map((r) => ({
                    ...r,
                    x: r.x * scaleRatio,
                    y: r.y * scaleRatio,
                    width: r.width * scaleRatio,
                    height: r.height * scaleRatio,
                }))
            );
            setCircles((prev) =>
                prev.map((c) => ({
                    ...c,
                    x: c.x * scaleRatio,
                    y: c.y * scaleRatio,
                    radius: c.radius * scaleRatio,
                }))
            );
            setFreeLines((prev) =>
                prev.map((l) => ({
                    ...l,
                    points: l.points.map((p) => p * scaleRatio),
                    strokeWidth: l.strokeWidth * scaleRatio,
                }))
            );
            setTexts((prev) =>
                prev.map((t) => ({
                    ...t,
                    x: t.x * scaleRatio,
                    y: t.y * scaleRatio,
                    fontSize: t.fontSize * scaleRatio,
                }))
            );
            setLines((prev) =>
                prev.map((l) => ({
                    ...l,
                    points: l.points.map((p) => p * scaleRatio),
                    strokeWidth: l.strokeWidth * scaleRatio,
                }))
            );
            setArrows((prev) =>
                prev.map((a) => ({
                    ...a,
                    points: a.points.map((p) => p * scaleRatio),
                    strokeWidth: a.strokeWidth * scaleRatio,
                }))
            );
            setImages((prev) =>
                prev.map((i) => ({
                    ...i,
                    x: i.x * scaleRatio,
                    y: i.y * scaleRatio,
                    width: i.width * scaleRatio,
                    height: i.height * scaleRatio,
                }))
            );
        };

        refit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bgImage, canvasAreaVersion]);

    const loadAnnotationData = async (data, scale) => {
        try {
            const parsed = JSON.parse(data);

            if (parsed.rectangles) {
                setRectangles(
                    parsed.rectangles.map((r) => ({
                        ...r,
                        x: r.x * scale,
                        y: r.y * scale,
                        width: r.width * scale,
                        height: r.height * scale,
                    }))
                );
            }

            if (parsed.circles) {
                setCircles(
                    parsed.circles.map((c) => ({
                        ...c,
                        x: c.x * scale,
                        y: c.y * scale,
                        radius: c.radius * scale,
                    }))
                );
            }

            if (parsed.freeLines) {
                setFreeLines(
                    parsed.freeLines.map((l) => ({
                        ...l,
                        points: l.points.map((p) => p * scale),
                        strokeWidth: l.strokeWidth * scale,
                    }))
                );
            }

            if (parsed.texts) {
                setTexts(
                    parsed.texts.map((t) => ({
                        ...t,
                        x: t.x * scale,
                        y: t.y * scale,
                        fontSize: t.fontSize * scale,
                        bgFill: t.bgFill || "#ff0000",
                        bgOpacity: t.bgOpacity !== undefined ? t.bgOpacity : 1,
                    }))
                );
            }

            if (parsed.lines) {
                setLines(
                    parsed.lines.map((l) => ({
                        ...l,
                        points: l.points.map((p) => p * scale),
                        strokeWidth: l.strokeWidth * scale,
                    }))
                );
            }

            if (parsed.arrows) {
                setArrows(
                    parsed.arrows.map((a) => ({
                        ...a,
                        points: a.points.map((p) => p * scale),
                        strokeWidth: a.strokeWidth * scale,
                    }))
                );
            }

            if (parsed.images && parsed.images.length > 0) {
                const loadedImages = await Promise.all(
                    parsed.images.map((imgData) => {
                        return new Promise((resolve, reject) => {
                            const img = new window.Image();
                            img.crossOrigin = "anonymous";
                            img.src = imgData.firebaseUrl;
                            img.onload = () => {
                                resolve({
                                    ...imgData,
                                    image: img,
                                    x: imgData.x * scale,
                                    y: imgData.y * scale,
                                    width: imgData.width * scale,
                                    height: imgData.height * scale,
                                });
                            };
                            img.onerror = () => {
                                console.error(
                                    "Failed to load image:",
                                    imgData.firebaseUrl
                                );
                                resolve(null);
                            };
                        });
                    })
                );

                const validImages = loadedImages.filter((img) => img !== null);
                setImages(validImages);
            }

            const allZIndices = [
                ...(parsed.rectangles || []).map((r) => r.zIndex || 0),
                ...(parsed.circles || []).map((c) => c.zIndex || 0),
                ...(parsed.freeLines || []).map((l) => l.zIndex || 0),
                ...(parsed.texts || []).map((t) => t.zIndex || 0),
                ...(parsed.lines || []).map((l) => l.zIndex || 0),
                ...(parsed.arrows || []).map((a) => a.zIndex || 0),
                ...(parsed.images || []).map((i) => i.zIndex || 0),
            ];

            nextZIndex.current = Math.max(0, ...allZIndices) + 1;

            setTimeout(() => {
                setIsLoadingAnnotation(false);
            }, 500);
        } catch (error) {
            console.error("Error loading annotation data:", error);
            setIsLoadingAnnotation(false);
        }
    };

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        if (isUndoRedoAction.current) {
            isUndoRedoAction.current = false;
            return;
        }

        const currentState = {
            rectangles,
            circles,
            freeLines,
            texts,
            lines,
            arrows,
            images: images.map((img) => ({
                id: img.id,
                x: img.x,
                y: img.y,
                width: img.width,
                height: img.height,
                firebaseUrl: img.firebaseUrl,
                storagePath: img.storagePath,
                zIndex: img.zIndex,
            })),
        };

        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(JSON.stringify(currentState));

        if (newHistory.length > MAX_HISTORY) {
            newHistory.shift();
        } else {
            setHistoryIndex((prev) => prev + 1);
        }

        setHistory(newHistory);
        // This effect writes the very state it reads, so listing history and
        // historyIndex here would make it retrigger itself forever. It is meant
        // to run only when the drawing changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rectangles, circles, freeLines, texts, lines, arrows, images]);

    const handleUndo = () => {
        if (historyIndex <= 0) return;

        const newIndex = historyIndex - 1;
        const previousState = JSON.parse(history[newIndex]);

        isUndoRedoAction.current = true;

        setRectangles(previousState.rectangles || []);
        setCircles(previousState.circles || []);
        setFreeLines(previousState.freeLines || []);
        setTexts(previousState.texts || []);
        setLines(previousState.lines || []);
        setArrows(previousState.arrows || []);

        if (previousState.images) {
            const loadPromises = previousState.images.map((imgData) => {
                const existingImg = images.find((img) => img.id === imgData.id);
                if (existingImg && existingImg.image) {
                    return Promise.resolve({
                        ...imgData,
                        image: existingImg.image,
                    });
                }

                return new Promise((resolve) => {
                    const img = new window.Image();
                    img.crossOrigin = "anonymous";
                    img.src = imgData.firebaseUrl;
                    img.onload = () => {
                        resolve({
                            ...imgData,
                            image: img,
                        });
                    };
                    img.onerror = () => {
                        console.error(
                            "Failed to load image:",
                            imgData.firebaseUrl
                        );
                        resolve(null);
                    };
                });
            });

            Promise.all(loadPromises).then((loadedImages) => {
                const validImages = loadedImages.filter((img) => img !== null);
                setImages(validImages);
            });
        } else {
            setImages([]);
        }

        setHistoryIndex(newIndex);
        setSelectedId(null);
    };

    const handleRedo = () => {
        if (historyIndex >= history.length - 1) return;

        const newIndex = historyIndex + 1;
        const nextState = JSON.parse(history[newIndex]);

        isUndoRedoAction.current = true;

        setRectangles(nextState.rectangles || []);
        setCircles(nextState.circles || []);
        setFreeLines(nextState.freeLines || []);
        setTexts(nextState.texts || []);
        setLines(nextState.lines || []);
        setArrows(nextState.arrows || []);

        if (nextState.images) {
            const loadPromises = nextState.images.map((imgData) => {
                const existingImg = images.find((img) => img.id === imgData.id);
                if (existingImg && existingImg.image) {
                    return Promise.resolve({
                        ...imgData,
                        image: existingImg.image,
                    });
                }

                return new Promise((resolve) => {
                    const img = new window.Image();
                    img.crossOrigin = "anonymous";
                    img.src = imgData.firebaseUrl;
                    img.onload = () => {
                        resolve({
                            ...imgData,
                            image: img,
                        });
                    };
                    img.onerror = () => {
                        console.error(
                            "Failed to load image:",
                            imgData.firebaseUrl
                        );
                        resolve(null);
                    };
                });
            });

            Promise.all(loadPromises).then((loadedImages) => {
                const validImages = loadedImages.filter((img) => img !== null);
                setImages(validImages);
            });
        } else {
            setImages([]);
        }

        setHistoryIndex(newIndex);
        setSelectedId(null);
    };

    const bringToFront = () => {
        if (!selectedId) return;

        const allShapes = getAllShapesSorted();
        const maxZ = Math.max(...allShapes.map((s) => s.zIndex || 0));
        const newZ = maxZ + 1;

        updateSelectedShapeProperty("zIndex", newZ);
    };

    const bringForward = () => {
        if (!selectedId) return;

        const allShapes = getAllShapesSorted();
        const selectedIndex = allShapes.findIndex((s) => s.id === selectedId);
        if (selectedIndex === -1 || selectedIndex === allShapes.length - 1)
            return;

        const nextShape = allShapes[selectedIndex + 1];
        const selectedShape = allShapes[selectedIndex];

        const selectedZ = selectedShape.zIndex || 0;
        const nextZ = nextShape.zIndex || 0;

        switch (nextShape.type) {
            case "rectangle":
                setRectangles((prev) =>
                    prev.map((r) =>
                        r.id === nextShape.id ? { ...r, zIndex: selectedZ } : r
                    )
                );
                break;
            case "circle":
                setCircles((prev) =>
                    prev.map((c) =>
                        c.id === nextShape.id ? { ...c, zIndex: selectedZ } : c
                    )
                );
                break;
            case "freeLine":
                setFreeLines((prev) =>
                    prev.map((l) =>
                        l.id === nextShape.id ? { ...l, zIndex: selectedZ } : l
                    )
                );
                break;
            case "text":
                setTexts((prev) =>
                    prev.map((t) =>
                        t.id === nextShape.id ? { ...t, zIndex: selectedZ } : t
                    )
                );
                break;
            case "line":
                setLines((prev) =>
                    prev.map((l) =>
                        l.id === nextShape.id ? { ...l, zIndex: selectedZ } : l
                    )
                );
                break;
            case "arrow":
                setArrows((prev) =>
                    prev.map((a) =>
                        a.id === nextShape.id ? { ...a, zIndex: selectedZ } : a
                    )
                );
                break;
            case "image":
                setImages((prev) =>
                    prev.map((i) =>
                        i.id === nextShape.id ? { ...i, zIndex: selectedZ } : i
                    )
                );
                break;
            default:
                break;
        }

        updateSelectedShapeProperty("zIndex", nextZ);
    };

    const sendBackward = () => {
        if (!selectedId) return;

        const allShapes = getAllShapesSorted();
        const selectedIndex = allShapes.findIndex((s) => s.id === selectedId);
        if (selectedIndex === -1 || selectedIndex === 0) return;

        const prevShape = allShapes[selectedIndex - 1];
        const selectedShape = allShapes[selectedIndex];

        const selectedZ = selectedShape.zIndex || 0;
        const prevZ = prevShape.zIndex || 0;

        switch (prevShape.type) {
            case "rectangle":
                setRectangles((prev) =>
                    prev.map((r) =>
                        r.id === prevShape.id ? { ...r, zIndex: selectedZ } : r
                    )
                );
                break;
            case "circle":
                setCircles((prev) =>
                    prev.map((c) =>
                        c.id === prevShape.id ? { ...c, zIndex: selectedZ } : c
                    )
                );
                break;
            case "freeLine":
                setFreeLines((prev) =>
                    prev.map((l) =>
                        l.id === prevShape.id ? { ...l, zIndex: selectedZ } : l
                    )
                );
                break;
            case "text":
                setTexts((prev) =>
                    prev.map((t) =>
                        t.id === prevShape.id ? { ...t, zIndex: selectedZ } : t
                    )
                );
                break;
            case "line":
                setLines((prev) =>
                    prev.map((l) =>
                        l.id === prevShape.id ? { ...l, zIndex: selectedZ } : l
                    )
                );
                break;
            case "arrow":
                setArrows((prev) =>
                    prev.map((a) =>
                        a.id === prevShape.id ? { ...a, zIndex: selectedZ } : a
                    )
                );
                break;
            case "image":
                setImages((prev) =>
                    prev.map((i) =>
                        i.id === prevShape.id ? { ...i, zIndex: selectedZ } : i
                    )
                );
                break;
            default:
                break;
        }

        updateSelectedShapeProperty("zIndex", prevZ);
    };

    const sendToBack = () => {
        if (!selectedId) return;

        const allShapes = getAllShapesSorted();
        const minZ = Math.min(...allShapes.map((s) => s.zIndex || 0));
        const newZ = minZ - 1;

        updateSelectedShapeProperty("zIndex", newZ);
    };

    const handleCopy = () => {
        if (!selectedId) return;

        const selectedRect = rectangles.find((r) => r.id === selectedId);
        const selectedCircle = circles.find((c) => c.id === selectedId);
        const selectedFreeLine = freeLines.find((l) => l.id === selectedId);
        const selectedText = texts.find((t) => t.id === selectedId);
        const selectedLine = lines.find((l) => l.id === selectedId);
        const selectedArrow = arrows.find((a) => a.id === selectedId);
        const selectedImage = images.find((i) => i.id === selectedId);

        if (selectedRect) {
            copiedShape.current = { type: "rectangle", data: selectedRect };
        } else if (selectedCircle) {
            copiedShape.current = { type: "circle", data: selectedCircle };
        } else if (selectedFreeLine) {
            copiedShape.current = { type: "freeLine", data: selectedFreeLine };
        } else if (selectedText) {
            copiedShape.current = { type: "text", data: selectedText };
        } else if (selectedLine) {
            copiedShape.current = { type: "line", data: selectedLine };
        } else if (selectedArrow) {
            copiedShape.current = { type: "arrow", data: selectedArrow };
        } else if (selectedImage) {
            copiedShape.current = { type: "image", data: selectedImage };
        }
    };

    const handlePaste = () => {
        if (!copiedShape.current) return;

        const offset = 20;
        const newId = `${copiedShape.current.type}-${Date.now()}`;
        const newZIndex = nextZIndex.current++;

        switch (copiedShape.current.type) {
            case "rectangle":
                const newRect = {
                    ...copiedShape.current.data,
                    id: newId,
                    x: copiedShape.current.data.x + offset,
                    y: copiedShape.current.data.y + offset,
                    zIndex: newZIndex,
                };
                setRectangles((prev) => [...prev, newRect]);
                setSelectedId(newId);
                break;
            case "circle":
                const newCircle = {
                    ...copiedShape.current.data,
                    id: newId,
                    x: copiedShape.current.data.x + offset,
                    y: copiedShape.current.data.y + offset,
                    zIndex: newZIndex,
                };
                setCircles((prev) => [...prev, newCircle]);
                setSelectedId(newId);
                break;
            case "freeLine":
                const newFreeLine = {
                    ...copiedShape.current.data,
                    id: newId,
                    points: copiedShape.current.data.points.map((p, i) =>
                        i % 2 === 0 ? p + offset : p + offset
                    ),
                    zIndex: newZIndex,
                };
                setFreeLines((prev) => [...prev, newFreeLine]);
                setSelectedId(newId);
                break;
            case "text":
                const newText = {
                    ...copiedShape.current.data,
                    id: newId,
                    x: copiedShape.current.data.x + offset,
                    y: copiedShape.current.data.y + offset,
                    zIndex: newZIndex,
                };
                setTexts((prev) => [...prev, newText]);
                setSelectedId(newId);
                break;
            case "line":
                const newLine = {
                    ...copiedShape.current.data,
                    id: newId,
                    points: copiedShape.current.data.points.map((p, i) =>
                        i % 2 === 0 ? p + offset : p + offset
                    ),
                    zIndex: newZIndex,
                };
                setLines((prev) => [...prev, newLine]);
                setSelectedId(newId);
                break;
            case "arrow":
                const newArrow = {
                    ...copiedShape.current.data,
                    id: newId,
                    points: copiedShape.current.data.points.map((p, i) =>
                        i % 2 === 0 ? p + offset : p + offset
                    ),
                    zIndex: newZIndex,
                };
                setArrows((prev) => [...prev, newArrow]);
                setSelectedId(newId);
                break;
            case "image":
                const newImage = {
                    ...copiedShape.current.data,
                    id: newId,
                    x: copiedShape.current.data.x + offset,
                    y: copiedShape.current.data.y + offset,
                    zIndex: newZIndex,
                };
                setImages((prev) => [...prev, newImage]);
                setSelectedId(newId);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (isEditingText.current) return;

            if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
                e.preventDefault();
                handleUndo();
            } else if (
                (e.ctrlKey || e.metaKey) &&
                (e.key === "y" || (e.key === "z" && e.shiftKey))
            ) {
                e.preventDefault();
                handleRedo();
            } else if ((e.ctrlKey || e.metaKey) && e.key === "c") {
                e.preventDefault();
                handleCopy();
            } else if ((e.ctrlKey || e.metaKey) && e.key === "v") {
                e.preventDefault();
                handlePaste();
            } else if (
                (e.ctrlKey || e.metaKey) &&
                e.shiftKey &&
                e.key === "]"
            ) {
                e.preventDefault();
                bringToFront();
            } else if (
                (e.ctrlKey || e.metaKey) &&
                !e.shiftKey &&
                e.key === "]"
            ) {
                e.preventDefault();
                bringForward();
            } else if (
                (e.ctrlKey || e.metaKey) &&
                !e.shiftKey &&
                e.key === "["
            ) {
                e.preventDefault();
                sendBackward();
            } else if (
                (e.ctrlKey || e.metaKey) &&
                e.shiftKey &&
                e.key === "["
            ) {
                e.preventDefault();
                sendToBack();
            } else if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                handleDeleteSelected();
            } else if (e.key === "Escape") {
                e.preventDefault();
                setSelectedId(null);
                isEditingText.current = false;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
        // The handlers below are rebuilt on every render, so depending on them
        // would tear down and re-add the listener constantly. The state they
        // actually read is listed here instead.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyIndex, history, selectedId]);

    useEffect(() => {
        if (!transformerRef.current || !shapeRef.current) return;

        const selectedRect = rectangles.find((r) => r.id === selectedId);
        const selectedCircle = circles.find((c) => c.id === selectedId);
        const selectedFreeLine = freeLines.find((l) => l.id === selectedId);
        const selectedText = texts.find((t) => t.id === selectedId);
        const selectedImage = images.find((i) => i.id === selectedId);

        if (selectedRect || selectedCircle || selectedImage || selectedText) {
            transformerRef.current.nodes([shapeRef.current]);
            if (selectedText) {
                // For text, only allow moving (no resize anchors)
                transformerRef.current.enabledAnchors([]);
            } else {
                transformerRef.current.enabledAnchors([
                    "top-left",
                    "top-right",
                    "bottom-left",
                    "bottom-right",
                ]);
            }
        } else if (selectedFreeLine) {
            transformerRef.current.nodes([shapeRef.current]);
            transformerRef.current.enabledAnchors([]);
        } else {
            transformerRef.current.nodes([]);
        }

        transformerRef.current.getLayer().batchDraw();
    }, [selectedId, rectangles, circles, freeLines, texts, images]);

    const handleMouseDown = (e) => {
        if (isEditingText.current) {
            return;
        }

        const target = e.target;
        const isStageClick = target === target.getStage();
        const isBgImageClick = target.name() === "background-image";

        if (!isStageClick && !isBgImageClick) {
            return;
        }

        if (tool === "select") {
            setSelectedId(null);
            return;
        }

        const stage = target.getStage();
        const pointerPos = stage.getPointerPosition();

        if (tool === "pen") {
            setNewFreeLine({
                id: `line-${Date.now()}`,
                points: [pointerPos.x, pointerPos.y],
                stroke: strokeColor,
                strokeWidth: lineWidth,
                lineCap: "round",
                lineJoin: "round",
                dash: lineIsDashed ? [10, 5] : [],
                zIndex: nextZIndex.current++,
            });
        } else if (tool === "rectangle") {
            setNewRect({
                x: pointerPos.x,
                y: pointerPos.y,
                width: 0,
                height: 0,
                id: `rect-${Date.now()}`,
                stroke: strokeColor,
                fill: fillColor,
                opacity: fillOpacity,
                zIndex: nextZIndex.current++,
            });
        } else if (tool === "circle") {
            setNewCircle({
                id: `circle-${Date.now()}`,
                x: pointerPos.x,
                y: pointerPos.y,
                radius: 0,
                stroke: strokeColor,
                fill: fillColor,
                opacity: fillOpacity,
                zIndex: nextZIndex.current++,
            });
        } else if (tool === "text") {
            const id = `text-${Date.now()}`;

            const newText = {
                id,
                x: pointerPos.x,
                y: pointerPos.y,
                text: "Double click to edit",
                fontSize,
                fontFamily,
                fill: strokeColor,
                bgFill: fillColor,
                bgOpacity: fillOpacity,
                zIndex: nextZIndex.current++,
            };

            setTexts((prev) => [...prev, newText]);

            // Auto-select the new text and switch to select tool
            setSelectedId(id);
            setTool("select");
        } else if (tool === "line") {
            const pointer = e.target.getStage().getPointerPosition();
            setNewLine({
                id: `line-${Date.now()}`,
                points: [pointer.x, pointer.y, pointer.x, pointer.y],
                stroke: strokeColor,
                strokeWidth: lineWidth,
                dash: lineIsDashed ? [10, 5] : [],
                zIndex: nextZIndex.current++,
            });
        } else if (tool === "arrow") {
            const pointer = e.target.getStage().getPointerPosition();
            setNewArrow({
                id: `arrow-${Date.now()}`,
                points: [pointer.x, pointer.y, pointer.x, pointer.y],
                stroke: strokeColor,
                fill: strokeColor,
                strokeWidth: lineWidth,
                dash: lineIsDashed ? [10, 5] : [],
                zIndex: nextZIndex.current++,
            });
        }
    };

    const handleMouseMove = (e) => {
        const stage = e.target.getStage();
        const pointerPos = stage.getPointerPosition();

        if (tool === "pen" && newFreeLine) {
            const stage = e.target.getStage();
            const pointer = stage.getPointerPosition();

            setNewFreeLine((prev) => ({
                ...prev,
                points: [...prev.points, pointer.x, pointer.y],
            }));
        } else if (tool === "rectangle" && newRect) {
            const width = pointerPos.x - newRect.x;
            const height = pointerPos.y - newRect.y;

            setNewRect({
                ...newRect,
                width,
                height,
            });
        } else if (tool === "circle" && newCircle) {
            const dx = pointerPos.x - newCircle.x;
            const dy = pointerPos.y - newCircle.y;
            const radius = Math.sqrt(dx * dx + dy * dy);
            setNewCircle({ ...newCircle, radius });
        } else if (tool === "line" && newLine) {
            const pointer = e.target.getStage().getPointerPosition();
            const [x1, y1] = newLine.points;
            setNewLine({
                ...newLine,
                points: [x1, y1, pointer.x, pointer.y],
            });
        } else if (tool === "arrow" && newArrow) {
            const pointer = e.target.getStage().getPointerPosition();
            const [x1, y1] = newArrow.points;
            setNewArrow({
                ...newArrow,
                points: [x1, y1, pointer.x, pointer.y],
            });
            return;
        }
    };

    const handleMouseUp = (e) => {
        if (tool === "pen" && newFreeLine) {
            setFreeLines((prev) => [...prev, newFreeLine]);
            setNewFreeLine(null);
            // Auto-select the new line and switch to select tool
            setSelectedId(newFreeLine.id);
            setTool("select");
        } else if (tool === "rectangle" && newRect) {
            if (Math.abs(newRect.width) >= 5 && Math.abs(newRect.height) >= 5) {
                let { x, y, width, height, stroke, fill, opacity, zIndex } =
                    newRect;
                if (width < 0) {
                    x += width;
                    width = Math.abs(width);
                }
                if (height < 0) {
                    y += height;
                    height = Math.abs(height);
                }

                const finalRect = {
                    id: newRect.id,
                    x,
                    y,
                    width,
                    height,
                    stroke,
                    fill,
                    opacity,
                    zIndex,
                };

                setRectangles((prev) => [...prev, finalRect]);

                // Auto-select the new rectangle and switch to select tool
                setSelectedId(newRect.id);
                setTool("select");
            }
        } else if (tool === "circle" && newCircle) {
            if (newCircle.radius >= 5) {
                setCircles((prev) => [...prev, newCircle]);
                // Auto-select the new circle and switch to select tool
                setSelectedId(newCircle.id);
                setTool("select");
            }
        } else if (tool === "line" && newLine) {
            const [x1, y1, x2, y2] = newLine.points;
            if (Math.hypot(x2 - x1, y2 - y1) < 5) {
                setNewLine(null);
                return;
            }

            setLines((prev) => [...prev, newLine]);
            setNewLine(null);
            // Auto-select the new line and switch to select tool
            setSelectedId(newLine.id);
            setTool("select");
        } else if (tool === "arrow" && newArrow) {
            const [x1, y1, x2, y2] = newArrow.points;
            if (Math.hypot(x2 - x1, y2 - y1) < 5) {
                setNewArrow(null);
                return;
            }

            setArrows((prev) => [...prev, newArrow]);
            setNewArrow(null);
            // Auto-select the new arrow and switch to select tool
            setSelectedId(newArrow.id);
            setTool("select");
        }

        clearInProgressShapes();
    };

    const handleClear = () => {
        clearInProgressShapes();

        images.forEach((img) => {
            if (img.storagePath) {
                deleteImageFromStorage(img.storagePath);
            }
        });

        setRectangles([]);
        setCircles([]);
        setFreeLines([]);
        setTexts([]);
        setLines([]);
        setArrows([]);
        setImages([]);
    };

    function clearInProgressShapes() {
        setNewRect(null);
        setNewCircle(null);
        setNewFreeLine(null);
        setNewLine(null);
        setNewArrow(null);
    }

    const handleDeleteSelected = async () => {
        if (!selectedId) return;

        const selectedImage = images.find((i) => i.id === selectedId);
        if (selectedImage && selectedImage.storagePath) {
            await deleteImageFromStorage(selectedImage.storagePath);
        }

        setRectangles((prev) => prev.filter((r) => r.id !== selectedId));
        setCircles((prev) => prev.filter((c) => c.id !== selectedId));
        setFreeLines((prev) => prev.filter((l) => l.id !== selectedId));
        setTexts((prev) => prev.filter((t) => t.id !== selectedId));
        setLines((prev) => prev.filter((l) => l.id !== selectedId));
        setArrows((prev) => prev.filter((a) => a.id !== selectedId));
        setImages((prev) => prev.filter((i) => i.id !== selectedId));
        setSelectedId(null);
    };

    const handleSave = () => {
        if (!stageRef.current) return;

        setIsSaving(true);
        setSelectedId(null);

        setTimeout(() => {
            const stage = stageRef.current;

            const dataUrl = stage.toDataURL({
                mimeType: "image/jpeg",
                quality: 0.92,
                pixelRatio: exportScale,
            });

            const annotationData = {
                rectangles: rectangles.map((r) => ({
                    ...r,
                    x: r.x * exportScale,
                    y: r.y * exportScale,
                    width: r.width * exportScale,
                    height: r.height * exportScale,
                })),
                circles: circles.map((c) => ({
                    ...c,
                    x: c.x * exportScale,
                    y: c.y * exportScale,
                    radius: c.radius * exportScale,
                })),
                freeLines: freeLines.map((l) => ({
                    ...l,
                    points: l.points.map((p) => p * exportScale),
                    strokeWidth: l.strokeWidth * exportScale,
                })),
                texts: texts.map((t) => ({
                    ...t,
                    x: t.x * exportScale,
                    y: t.y * exportScale,
                    fontSize: t.fontSize * exportScale,
                })),
                lines: lines.map((l) => ({
                    ...l,
                    points: l.points.map((p) => p * exportScale),
                    strokeWidth: l.strokeWidth * exportScale,
                })),
                arrows: arrows.map((a) => ({
                    ...a,
                    points: a.points.map((p) => p * exportScale),
                    strokeWidth: a.strokeWidth * exportScale,
                })),
                images: images.map((i) => ({
                    id: i.id,
                    x: i.x * exportScale,
                    y: i.y * exportScale,
                    width: i.width * exportScale,
                    height: i.height * exportScale,
                    firebaseUrl: i.firebaseUrl,
                    storagePath: i.storagePath,
                    zIndex: i.zIndex,
                })),
            };

            onSave(dataUrl, JSON.stringify(annotationData));

            setIsSaving(false);
        }, 100);
    };

    const handleTextEdit = (id) => {
        isEditingText.current = true;

        const textNode = texts.find((t) => t.id === id);
        if (!textNode) return;

        const textarea = document.createElement("textarea");
        textarea.value = textNode.text;
        textarea.style.position = "absolute";
        textarea.style.top = `${textNode.y}px`;
        textarea.style.left = `${textNode.x}px`;
        textarea.style.fontSize = `${textNode.fontSize}px`;
        textarea.style.fontFamily = textNode.fontFamily;
        textarea.style.color = textNode.fill;
        textarea.style.border = "2px solid blue";
        textarea.style.padding = "4px";
        textarea.style.margin = "0";
        textarea.style.overflow = "hidden";
        textarea.style.background = "white";
        textarea.style.outline = "none";
        textarea.style.resize = "none";
        textarea.style.lineHeight = "1";
        textarea.style.transformOrigin = "left top";

        const stageContainer = stageRef.current.container();
        stageContainer.appendChild(textarea);

        textarea.focus();
        textarea.select();

        const removeTextarea = () => {
            isEditingText.current = false;

            setTexts((prev) =>
                prev.map((t) =>
                    t.id === id ? { ...t, text: textarea.value } : t
                )
            );

            textarea.removeEventListener("blur", removeTextarea);
            textarea.removeEventListener("keydown", handleKeyDown);
            stageContainer.removeChild(textarea);
        };

        const handleKeyDown = (e) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                removeTextarea();
            } else if (e.key === "Escape") {
                e.preventDefault();
                removeTextarea();
            }
        };

        textarea.addEventListener("blur", removeTextarea);
        textarea.addEventListener("keydown", handleKeyDown);
    };

    return (
        <>
            {/* Loading Dialog */}
            <Dialog
                open={isImportingImage || isLoadingAnnotation || isSaving}
                PaperProps={{
                    style: {
                        backgroundColor: "transparent",
                        boxShadow: "none",
                    },
                }}
            >
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ color: "white" }}>
                        {isImportingImage && "Importing image..."}
                        {isLoadingAnnotation && "Loading annotation..."}
                        {isSaving && "Saving..."}
                    </Typography>
                </DialogContent>
            </Dialog>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageImport}
            />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mb: 1,
                    p: 1,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    boxShadow: 1,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    <ToggleButtonGroup
                        value={tool}
                        exclusive
                        onChange={(e, val) => val && setTool(val)}
                        size="small"
                    >
                        {TOOL_OPTIONS.map(({ value, icon, label }) => (
                            <Tooltip key={value} title={label} arrow>
                                <ToggleButton value={value}>
                                    {icon}
                                </ToggleButton>
                            </Tooltip>
                        ))}
                    </ToggleButtonGroup>

                    <Box
                        sx={{
                            borderLeft: "1px solid #ccc",
                            height: 30,
                            mx: 0.5,
                        }}
                    />

                    <Tooltip title="Import Image">
                        <ToggleButton
                            value="import"
                            size="small"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <ImageIcon />
                        </ToggleButton>
                    </Tooltip>

                    <Box
                        sx={{
                            borderLeft: "1px solid #ccc",
                            height: 30,
                            mx: 0.5,
                        }}
                    />

                    <Tooltip title={`Undo (Ctrl+Z)`}>
                        <span>
                            <ToggleButton
                                onClick={handleUndo}
                                value="undo"
                                size="small"
                                disabled={historyIndex <= 0}
                            >
                                <UndoIcon />
                            </ToggleButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={`Redo (Ctrl+Y)`}>
                        <span>
                            <ToggleButton
                                onClick={handleRedo}
                                value="redo"
                                size="small"
                                disabled={historyIndex >= history.length - 1}
                            >
                                <RedoIcon />
                            </ToggleButton>
                        </span>
                    </Tooltip>

                    <Box
                        sx={{
                            borderLeft: "1px solid #ccc",
                            height: 30,
                            mx: 0.5,
                        }}
                    />

                    <Tooltip title="Copy (Ctrl+C)">
                        <span>
                            <ToggleButton
                                onClick={handleCopy}
                                value="copy"
                                size="small"
                                disabled={!selectedId}
                            >
                                <ContentCopyIcon />
                            </ToggleButton>
                        </span>
                    </Tooltip>

                    <Box
                        sx={{
                            borderLeft: "1px solid #ccc",
                            height: 30,
                            mx: 0.5,
                        }}
                    />

                    <Tooltip title="Bring to Front (Ctrl+Shift+])">
                        <span>
                            <ToggleButton
                                onClick={bringToFront}
                                value="bringToFront"
                                size="small"
                                disabled={!selectedId}
                            >
                                <FlipToFrontIcon />
                            </ToggleButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Send to Back (Ctrl+Shift+[)">
                        <span>
                            <ToggleButton
                                onClick={sendToBack}
                                value="sendToBack"
                                size="small"
                                disabled={!selectedId}
                            >
                                <FlipToBackIcon />
                            </ToggleButton>
                        </span>
                    </Tooltip>

                    <Box
                        sx={{
                            borderLeft: "1px solid #ccc",
                            height: 30,
                            mx: 0.5,
                        }}
                    />

                    <Tooltip title="Clear canvas">
                        <ToggleButton
                            onClick={handleClear}
                            value="clear"
                            size="small"
                        >
                            <CancelPresentationIcon />
                        </ToggleButton>
                    </Tooltip>

                    <Tooltip title="Delete (Del)">
                        <ToggleButton
                            onClick={handleDeleteSelected}
                            value="delete"
                            size="small"
                            disabled={!selectedId}
                        >
                            <DeleteIcon />
                        </ToggleButton>
                    </Tooltip>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "center",
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="stroke-color-label">
                            Stroke / Text Color
                        </InputLabel>
                        <Select
                            labelId="stroke-color-label"
                            value={strokeColor}
                            label="Stroke / Text Color"
                            onChange={(e) =>
                                handleStrokeColorChange(e.target.value)
                            }
                        >
                            {COLORS.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "inline-block",
                                            width: 20,
                                            height: 20,
                                            bgcolor: value,
                                            mr: 1,
                                            verticalAlign: "middle",
                                            border:
                                                value === "#ffffff" ||
                                                value === "#00000000"
                                                    ? "1px solid #ccc"
                                                    : "none",
                                        }}
                                    />
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="fill-color-label">
                            Fill / Text Background
                        </InputLabel>
                        <Select
                            labelId="fill-color-label"
                            value={fillColor}
                            label="Fill / Text Background"
                            onChange={(e) =>
                                handleFillColorChange(e.target.value)
                            }
                        >
                            {COLORS.map(({ value, label }) => (
                                <MenuItem key={value} value={value}>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "inline-block",
                                            width: 20,
                                            height: 20,
                                            bgcolor: value,
                                            mr: 1,
                                            verticalAlign: "middle",
                                            border:
                                                value === "#ffffff" ||
                                                value === "#00000000"
                                                    ? "1px solid #ccc"
                                                    : "none",
                                        }}
                                    />
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="fill-opacity-label">
                            Fill Opacity
                        </InputLabel>
                        <Select
                            labelId="fill-opacity-label"
                            value={fillOpacity}
                            label="Fill Opacity"
                            onChange={(e) =>
                                handleFillOpacityChange(e.target.value)
                            }
                        >
                            <MenuItem value={0}>0%</MenuItem>
                            <MenuItem value={0.25}>25%</MenuItem>
                            <MenuItem value={0.5}>50%</MenuItem>
                            <MenuItem value={0.75}>75%</MenuItem>
                            <MenuItem value={1}>100%</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="line-width-label">
                            Line Width
                        </InputLabel>
                        <Select
                            labelId="line-width-label"
                            value={lineWidth}
                            label="Line Width"
                            onChange={(e) =>
                                handleLineWidthChange(e.target.value)
                            }
                        >
                            <MenuItem value={1}>1px</MenuItem>
                            <MenuItem value={2}>2px</MenuItem>
                            <MenuItem value={3}>3px</MenuItem>
                            <MenuItem value={4}>4px</MenuItem>
                            <MenuItem value={5}>5px</MenuItem>
                            <MenuItem value={6}>6px</MenuItem>
                            <MenuItem value={8}>8px</MenuItem>
                            <MenuItem value={10}>10px</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="line-style-label">
                            Line Style
                        </InputLabel>
                        <Select
                            labelId="line-style-label"
                            value={lineIsDashed ? "dashed" : "solid"}
                            label="Line Style"
                            onChange={(e) =>
                                handleLineIsDashedChange(
                                    e.target.value === "dashed"
                                )
                            }
                        >
                            <MenuItem value="solid">Solid</MenuItem>
                            <MenuItem value="dashed">Dashed</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="font-family-label">
                            Font Family
                        </InputLabel>
                        <Select
                            labelId="font-family-label"
                            value={fontFamily}
                            label="Font Family"
                            onChange={(e) =>
                                handleFontFamilyChange(e.target.value)
                            }
                        >
                            <MenuItem value="Arial">Arial</MenuItem>
                            <MenuItem value="Courier New">Courier New</MenuItem>
                            <MenuItem value="Georgia">Georgia</MenuItem>
                            <MenuItem value="Times New Roman">
                                Times New Roman
                            </MenuItem>
                            <MenuItem value="Verdana">Verdana</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="font-size-label">Font Size</InputLabel>
                        <Select
                            labelId="font-size-label"
                            value={fontSize}
                            label="Font Size"
                            onChange={(e) =>
                                handleFontSizeChange(e.target.value)
                            }
                        >
                            <MenuItem value={12}>12pt</MenuItem>
                            <MenuItem value={14}>14pt</MenuItem>
                            <MenuItem value={16}>16pt</MenuItem>
                            <MenuItem value={18}>18pt</MenuItem>
                            <MenuItem value={20}>20pt</MenuItem>
                            <MenuItem value={24}>24pt</MenuItem>
                            <MenuItem value={28}>28pt</MenuItem>
                            <MenuItem value={32}>32pt</MenuItem>
                            <MenuItem value={36}>36pt</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            <Box
                sx={{
                    paddingBottom: 1,
                }}
            >
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={isSaving}
                >
                    Save Annotation to Playlist
                </Button>
            </Box>

            <Box
                ref={canvasAreaRef}
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    backgroundColor: "background.media",
                    minHeight: MIN_CANVAS_HEIGHT,
                    padding: 2,
                    overflow: "auto",
                }}
            >
                {bgImage ? (
                    <Stage
                        ref={stageRef}
                        width={stageDimensions.width}
                        height={stageDimensions.height}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        style={{
                            backgroundColor: "#000",
                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        }}
                    >
                        <Layer>
                            {bgImage && (
                                <KonvaImage
                                    image={bgImage}
                                    x={0}
                                    y={0}
                                    width={stageDimensions.width}
                                    height={stageDimensions.height}
                                    name="background-image"
                                />
                            )}
                        </Layer>

                        <Layer>
                            {getAllShapesSorted().map((shape) => {
                                const isSelected = shape.id === selectedId;

                                switch (shape.type) {
                                    case "freeLine":
                                        return (
                                            <Line
                                                key={shape.id}
                                                ref={
                                                    isSelected
                                                        ? shapeRef
                                                        : undefined
                                                }
                                                points={shape.points}
                                                stroke={shape.stroke}
                                                strokeWidth={shape.strokeWidth}
                                                lineCap={shape.lineCap}
                                                lineJoin={shape.lineJoin}
                                                dash={shape.dash}
                                                listening={tool === "select"}
                                                onClick={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                onTap={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                draggable={
                                                    tool === "select" &&
                                                    isSelected
                                                }
                                                onDragEnd={(e) => {
                                                    setFreeLines((prev) =>
                                                        prev.map((l) =>
                                                            l.id === shape.id
                                                                ? {
                                                                      ...l,
                                                                      x: e.target.x(),
                                                                      y: e.target.y(),
                                                                  }
                                                                : l
                                                        )
                                                    );
                                                }}
                                            />
                                        );

                                    case "rectangle":
                                        return (
                                            <Rect
                                                key={shape.id}
                                                ref={
                                                    isSelected
                                                        ? shapeRef
                                                        : undefined
                                                }
                                                x={shape.x}
                                                y={shape.y}
                                                width={shape.width}
                                                height={shape.height}
                                                stroke={shape.stroke}
                                                strokeWidth={2}
                                                fill={shape.fill}
                                                opacity={shape.opacity}
                                                rotation={shape.rotation || 0}
                                                listening={tool === "select"}
                                                onClick={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                onTap={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                draggable={
                                                    tool === "select" &&
                                                    isSelected
                                                }
                                                onDragEnd={(e) => {
                                                    setRectangles((prev) =>
                                                        prev.map((r) =>
                                                            r.id === shape.id
                                                                ? {
                                                                      ...r,
                                                                      x: e.target.x(),
                                                                      y: e.target.y(),
                                                                  }
                                                                : r
                                                        )
                                                    );
                                                }}
                                                onTransformEnd={(e) => {
                                                    const node = e.target;
                                                    const scaleX =
                                                        node.scaleX();
                                                    const scaleY =
                                                        node.scaleY();

                                                    node.scaleX(1);
                                                    node.scaleY(1);

                                                    setRectangles((prev) =>
                                                        prev.map((r) =>
                                                            r.id === shape.id
                                                                ? {
                                                                      ...r,
                                                                      x: node.x(),
                                                                      y: node.y(),
                                                                      width: Math.max(
                                                                          5,
                                                                          node.width() *
                                                                              scaleX
                                                                      ),
                                                                      height: Math.max(
                                                                          5,
                                                                          node.height() *
                                                                              scaleY
                                                                      ),
                                                                      rotation:
                                                                          node.rotation(),
                                                                  }
                                                                : r
                                                        )
                                                    );
                                                }}
                                            />
                                        );

                                    case "circle":
                                        return (
                                            <Circle
                                                key={shape.id}
                                                ref={
                                                    isSelected
                                                        ? shapeRef
                                                        : undefined
                                                }
                                                x={shape.x}
                                                y={shape.y}
                                                radius={shape.radius}
                                                stroke={shape.stroke}
                                                strokeWidth={2}
                                                fill={shape.fill}
                                                opacity={shape.opacity}
                                                rotation={shape.rotation || 0}
                                                listening={tool === "select"}
                                                onClick={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                onTap={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                draggable={
                                                    tool === "select" &&
                                                    isSelected
                                                }
                                                onDragEnd={(e) => {
                                                    setCircles((prev) =>
                                                        prev.map((c) =>
                                                            c.id === shape.id
                                                                ? {
                                                                      ...c,
                                                                      x: e.target.x(),
                                                                      y: e.target.y(),
                                                                  }
                                                                : c
                                                        )
                                                    );
                                                }}
                                                onTransformEnd={(e) => {
                                                    const node = e.target;
                                                    const scaleX =
                                                        node.scaleX();
                                                    const scaleY =
                                                        node.scaleY();

                                                    node.scaleX(1);
                                                    node.scaleY(1);

                                                    const newRadius = Math.max(
                                                        5,
                                                        node.radius() *
                                                            Math.max(
                                                                scaleX,
                                                                scaleY
                                                            )
                                                    );

                                                    setCircles((prev) =>
                                                        prev.map((c) =>
                                                            c.id === shape.id
                                                                ? {
                                                                      ...c,
                                                                      x: node.x(),
                                                                      y: node.y(),
                                                                      radius: newRadius,
                                                                      rotation:
                                                                          node.rotation(),
                                                                  }
                                                                : c
                                                        )
                                                    );
                                                }}
                                            />
                                        );

                                    case "line":
                                        return (
                                            <Line
                                                key={shape.id}
                                                points={shape.points}
                                                stroke={shape.stroke}
                                                strokeWidth={shape.strokeWidth}
                                                dash={shape.dash}
                                                listening={tool === "select"}
                                                onClick={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                onTap={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                            />
                                        );

                                    case "arrow":
                                        return (
                                            <Arrow
                                                key={shape.id}
                                                points={shape.points}
                                                stroke={shape.stroke}
                                                fill={shape.fill}
                                                strokeWidth={shape.strokeWidth}
                                                pointerLength={10}
                                                pointerWidth={10}
                                                dash={shape.dash}
                                                listening={tool === "select"}
                                                onClick={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                onTap={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                            />
                                        );

                                    case "text":
                                        return (
                                            <Group
                                                key={shape.id}
                                                ref={
                                                    isSelected
                                                        ? shapeRef
                                                        : undefined
                                                }
                                                x={shape.x}
                                                y={shape.y}
                                                rotation={shape.rotation || 0}
                                                listening={tool === "select"}
                                                onClick={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                onTap={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                onDblClick={() =>
                                                    handleTextEdit(shape.id)
                                                }
                                                onDblTap={() =>
                                                    handleTextEdit(shape.id)
                                                }
                                                draggable={
                                                    tool === "select" &&
                                                    isSelected
                                                }
                                                onDragEnd={(e) => {
                                                    setTexts((prev) =>
                                                        prev.map((t) =>
                                                            t.id === shape.id
                                                                ? {
                                                                      ...t,
                                                                      x: e.target.x(),
                                                                      y: e.target.y(),
                                                                  }
                                                                : t
                                                        )
                                                    );
                                                }}
                                                onTransformEnd={(e) => {
                                                    const node = e.target;
                                                    setTexts((prev) =>
                                                        prev.map((t) =>
                                                            t.id === shape.id
                                                                ? {
                                                                      ...t,
                                                                      x: node.x(),
                                                                      y: node.y(),
                                                                      rotation:
                                                                          node.rotation(),
                                                                  }
                                                                : t
                                                        )
                                                    );
                                                }}
                                            >
                                                {/* Background Rectangle */}
                                                <Rect
                                                    x={-TEXT_PADDING}
                                                    y={-TEXT_PADDING}
                                                    width={
                                                        shape.text.length *
                                                            shape.fontSize *
                                                            0.6 +
                                                        TEXT_PADDING * 2
                                                    }
                                                    height={
                                                        shape.fontSize +
                                                        TEXT_PADDING * 2
                                                    }
                                                    fill={
                                                        shape.bgFill ||
                                                        "#ff0000"
                                                    }
                                                    opacity={
                                                        shape.bgOpacity !==
                                                        undefined
                                                            ? shape.bgOpacity
                                                            : 1
                                                    }
                                                    cornerRadius={
                                                        TEXT_CORNER_RADIUS
                                                    }
                                                />
                                                {/* Text */}
                                                <Text
                                                    text={shape.text}
                                                    fontSize={shape.fontSize}
                                                    fontFamily={
                                                        shape.fontFamily
                                                    }
                                                    fill={shape.fill}
                                                />
                                            </Group>
                                        );

                                    case "image":
                                        return (
                                            <KonvaImage
                                                key={shape.id}
                                                ref={
                                                    isSelected
                                                        ? shapeRef
                                                        : undefined
                                                }
                                                image={shape.image}
                                                x={shape.x}
                                                y={shape.y}
                                                width={shape.width}
                                                height={shape.height}
                                                rotation={shape.rotation || 0}
                                                listening={tool === "select"}
                                                onClick={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                onTap={() =>
                                                    tool === "select" &&
                                                    setSelectedId(shape.id)
                                                }
                                                draggable={
                                                    tool === "select" &&
                                                    isSelected
                                                }
                                                onDragEnd={(e) => {
                                                    setImages((prev) =>
                                                        prev.map((i) =>
                                                            i.id === shape.id
                                                                ? {
                                                                      ...i,
                                                                      x: e.target.x(),
                                                                      y: e.target.y(),
                                                                  }
                                                                : i
                                                        )
                                                    );
                                                }}
                                                onTransformEnd={(e) => {
                                                    const node = e.target;
                                                    const scaleX =
                                                        node.scaleX();
                                                    const scaleY =
                                                        node.scaleY();

                                                    node.scaleX(1);
                                                    node.scaleY(1);

                                                    setImages((prev) =>
                                                        prev.map((i) =>
                                                            i.id === shape.id
                                                                ? {
                                                                      ...i,
                                                                      x: node.x(),
                                                                      y: node.y(),
                                                                      width: Math.max(
                                                                          5,
                                                                          node.width() *
                                                                              scaleX
                                                                      ),
                                                                      height: Math.max(
                                                                          5,
                                                                          node.height() *
                                                                              scaleY
                                                                      ),
                                                                      rotation:
                                                                          node.rotation(),
                                                                  }
                                                                : i
                                                        )
                                                    );
                                                }}
                                            />
                                        );

                                    default:
                                        return null;
                                }
                            })}

                            {newFreeLine && (
                                <Line
                                    points={newFreeLine.points}
                                    stroke={newFreeLine.stroke}
                                    strokeWidth={newFreeLine.strokeWidth}
                                    lineCap={newFreeLine.lineCap}
                                    lineJoin={newFreeLine.lineJoin}
                                    dash={newFreeLine.dash}
                                />
                            )}

                            {newRect && (
                                <Rect
                                    x={newRect.x}
                                    y={newRect.y}
                                    width={newRect.width}
                                    height={newRect.height}
                                    stroke={newRect.stroke}
                                    strokeWidth={1}
                                    fill={newRect.fill}
                                    opacity={newRect.opacity}
                                    dash={[4, 4]}
                                />
                            )}

                            {newCircle && (
                                <Circle
                                    x={newCircle.x}
                                    y={newCircle.y}
                                    radius={newCircle.radius}
                                    stroke={newCircle.stroke}
                                    strokeWidth={1}
                                    fill={newCircle.fill}
                                    opacity={newCircle.opacity}
                                    dash={[4, 4]}
                                />
                            )}

                            {newLine && (
                                <Line
                                    points={newLine.points}
                                    stroke={newLine.stroke}
                                    strokeWidth={newLine.strokeWidth}
                                    dash={newLine.dash}
                                />
                            )}

                            {newArrow && (
                                <Arrow
                                    points={newArrow.points}
                                    stroke={newArrow.stroke}
                                    fill={newArrow.fill}
                                    strokeWidth={newArrow.strokeWidth}
                                    pointerLength={10}
                                    pointerWidth={10}
                                    dash={newArrow.dash}
                                />
                            )}

                            {tool === "select" && selectedId && (
                                <Transformer
                                    ref={transformerRef}
                                    keepRatio={false}
                                    boundBoxFunc={(oldBox, newBox) => {
                                        if (
                                            newBox.width < 5 ||
                                            newBox.height < 5
                                        )
                                            return oldBox;
                                        return newBox;
                                    }}
                                />
                            )}

                            {tool === "select" &&
                                selectedId &&
                                arrows.find((a) => a.id === selectedId) &&
                                (() => {
                                    const arrow = arrows.find(
                                        (a) => a.id === selectedId
                                    );
                                    const [x1, y1, x2, y2] = arrow.points;

                                    return (
                                        <>
                                            <Circle
                                                x={x1}
                                                y={y1}
                                                radius={6}
                                                fill="white"
                                                stroke="black"
                                                strokeWidth={1}
                                                draggable
                                                onDragMove={(e) => {
                                                    const newX = e.target.x();
                                                    const newY = e.target.y();
                                                    const updatedPoints = [
                                                        newX,
                                                        newY,
                                                        x2,
                                                        y2,
                                                    ];
                                                    setArrows((prev) =>
                                                        prev.map((a) =>
                                                            a.id === arrow.id
                                                                ? {
                                                                      ...a,
                                                                      points: updatedPoints,
                                                                  }
                                                                : a
                                                        )
                                                    );
                                                }}
                                            />
                                            <Circle
                                                x={x2}
                                                y={y2}
                                                radius={6}
                                                fill="white"
                                                stroke="black"
                                                strokeWidth={1}
                                                draggable
                                                onDragMove={(e) => {
                                                    const newX = e.target.x();
                                                    const newY = e.target.y();
                                                    const updatedPoints = [
                                                        x1,
                                                        y1,
                                                        newX,
                                                        newY,
                                                    ];
                                                    setArrows((prev) =>
                                                        prev.map((a) =>
                                                            a.id === arrow.id
                                                                ? {
                                                                      ...a,
                                                                      points: updatedPoints,
                                                                  }
                                                                : a
                                                        )
                                                    );
                                                }}
                                            />
                                        </>
                                    );
                                })()}

                            {tool === "select" &&
                                selectedId &&
                                lines.find((a) => a.id === selectedId) &&
                                (() => {
                                    const line = lines.find(
                                        (a) => a.id === selectedId
                                    );
                                    const [x1, y1, x2, y2] = line.points;

                                    return (
                                        <>
                                            <Circle
                                                x={x1}
                                                y={y1}
                                                radius={6}
                                                fill="white"
                                                stroke="black"
                                                strokeWidth={1}
                                                draggable
                                                onDragMove={(e) => {
                                                    const newX = e.target.x();
                                                    const newY = e.target.y();
                                                    const updatedPoints = [
                                                        newX,
                                                        newY,
                                                        x2,
                                                        y2,
                                                    ];
                                                    setLines((prev) =>
                                                        prev.map((a) =>
                                                            a.id === line.id
                                                                ? {
                                                                      ...a,
                                                                      points: updatedPoints,
                                                                  }
                                                                : a
                                                        )
                                                    );
                                                }}
                                            />
                                            <Circle
                                                x={x2}
                                                y={y2}
                                                radius={6}
                                                fill="white"
                                                stroke="black"
                                                strokeWidth={1}
                                                draggable
                                                onDragMove={(e) => {
                                                    const newX = e.target.x();
                                                    const newY = e.target.y();
                                                    const updatedPoints = [
                                                        x1,
                                                        y1,
                                                        newX,
                                                        newY,
                                                    ];
                                                    setLines((prev) =>
                                                        prev.map((a) =>
                                                            a.id === line.id
                                                                ? {
                                                                      ...a,
                                                                      points: updatedPoints,
                                                                  }
                                                                : a
                                                        )
                                                    );
                                                }}
                                            />
                                        </>
                                    );
                                })()}
                        </Layer>
                    </Stage>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 400,
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            Loading canvas...
                        </Typography>
                    </Box>
                )}
            </Box>
        </>
    );
}
